import { apiClient } from './client'
import type {
  Alert,
  AlertDetail,
  AlertSeverity,
  AlertStatus,
  AlertType,
  ResolveAlertRequest,
  UnreadCountResponse,
} from '@/types'

export interface AlertsParams {
  status?: AlertStatus
  severity?: AlertSeverity
  alert_type?: AlertType
  member_id?: string
  page?: number
  page_size?: number
}

export interface AlertsResponse {
  data: Alert[]
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_count: number
  }
}

export interface EscalateRequest {
  member_ids: string[]
  message: string
}

export const alertsApi = {
  // Get alerts list
  getAlerts: async (familyId: string, params?: AlertsParams): Promise<AlertsResponse> => {
    const response = await apiClient.get<{ results: Alert[]; count: number }>(
      `/families/${familyId}/alerts`, {
        params: params as Record<string, string | number | boolean | undefined>,
      }
    )
    const pageSize = Math.min(params?.page_size ?? 20, 100)
    return {
      data: response.results,
      pagination: {
        page: params?.page ?? 1,
        page_size: pageSize,
        total_count: response.count,
        total_pages: Math.ceil(response.count / pageSize),
      },
    }
  },

  // Get single alert
  getAlert: (_familyId: string, alertId: string) =>
    apiClient.get<AlertDetail>(`/alerts/${alertId}`),

  // Acknowledge an alert
  acknowledgeAlert: (_familyId: string, alertId: string, data?: { notes?: string }) =>
    apiClient.put<AlertDetail>(`/alerts/${alertId}/acknowledge`, data),

  // Resolve an alert
  resolveAlert: (_familyId: string, alertId: string, data: ResolveAlertRequest) =>
    apiClient.put<AlertDetail>(`/alerts/${alertId}/resolve`, data),

  // Escalate an alert
  escalateAlert: (_familyId: string, alertId: string, data: EscalateRequest) =>
    apiClient.post<AlertDetail>(`/alerts/${alertId}/escalate`, data),

  // Get unread count (user-scoped, not family-scoped)
  getUnreadCount: (familyId?: string, memberId?: string) =>
    apiClient.get<UnreadCountResponse>(`/alerts/unread-count`, { params: { family_id: familyId, member_id: memberId } }),
}
